import { Injectable, inject } from '@angular/core';
import { DvCheckboxElement } from '@devinco/ui/legacy';
import { TranslateService } from '@ngx-translate/core';
import { OnCallStatus } from '@pages/on-call-page/constants';
import {
  CustomOnCallMapSettings,
  OnCallDto,
} from '@pages/on-call-page/interfaces';
import { LocationOnCallStatusIcon } from '@shared/components/timed-marker-map-menu-panel/constants';
import TimedMapMarkerUtils from '@shared/components/timed-marker-map-menu-panel/timed-marker.utils';
import { MapMarker, MapInput } from '@features/map/interfaces';
import MarkerPopupUtils from '@features/map/utils/marker-popup.utils';
import DateUtils from '@shared/utils/date.utils';
import { PointExpression } from 'leaflet';
import { DvDynamicFormElement } from '@devinco/ui/legacy';
@Injectable({ providedIn: 'root' })
export class OnCallMapService {
  private translate = inject(TranslateService);

  getCustomMapSettingsElements = (): DvDynamicFormElement[] => [
    new DvCheckboxElement({
      colspan: 12,
      key: 'Description',
      checkboxLabel: 'OnCallMapSettings.Description',
      rowspan: 2,
      extra: { defaultValue: true },
    }),
    new DvCheckboxElement({
      colspan: 12,
      key: 'UserDescription',
      checkboxLabel: 'OnCallMapSettings.UserDescription',
      rowspan: 2,
      extra: { defaultValue: true },
    }),
    new DvCheckboxElement({
      colspan: 12,
      key: 'UserTag',
      checkboxLabel: 'OnCallMapSettings.UserTag',
      rowspan: 2,
    }),
    new DvCheckboxElement({
      colspan: 12,
      key: 'VehicleTag',
      checkboxLabel: 'OnCallMapSettings.VehicleTag',
      rowspan: 2,
    }),
  ];

  getMapMarkers(
    items: OnCallDto[],
    customSettingsFormValue: CustomOnCallMapSettings
  ): MapInput[] {
    const points: MapMarker[] = items
      .filter(item => item.GPSLat && item.GPSLon)
      .map(item => {
        const timeDifference = DateUtils.getTimeDifferenceInMinutes(
          item.LastActivityTime!,
          DateUtils.getCurrentNOTime()
        );

        let iconUrl = '';
        if (item.IsVehicle) {
          iconUrl = TimedMapMarkerUtils.getEntityIconByTimeDifference(
            timeDifference,
            'Vehicle'
          );
        } else if (item.IsLocation) {
          iconUrl = this.getLocationIconByOnCallStatus(item.OnCallStatus);
        }

        return {
          lat: item.GPSLat!,
          lng: item.GPSLon!,
          options: {
            rotationAngle: item.LastCourse,
            riseOnHover: true,
            tooltipValue: this.getTooltipFromMapSettings(
              item,
              customSettingsFormValue
            ),
            isToolTipPermanent: true,
            customMarker: {
              iconUrl: iconUrl,
              iconSize: item.IsLocation
                ? [50, 50]
                : ([30, 30] as PointExpression),
            },
            popup: {
              customElement: item.IsVehicle
                ? MarkerPopupUtils.getVehiclePopupElementFromRegistry(
                    {
                      ...item,
                      VehicleDescription: item.Description,
                      UserMobile: item.Mobile,
                    },
                    iconUrl
                  )
                : MarkerPopupUtils.getLocationPopupElementFromRegistry(item),
              popupTableData: {
                iconHeader: this.translate.instant('General.Icon'),
                contentData: this.constructClusterPopupTableData(item, iconUrl),
              },
            },
          },
        };
      });

    return [{ points }];
  }

  private constructClusterPopupTableData(
    onCallItem: OnCallDto,
    iconUrl: string
  ): { [header: string]: string } {
    const popupData: { [header: string]: string } = {};
    popupData[this.translate.instant('General.Icon')] = iconUrl;
    popupData[this.translate.instant('OnCallTable.Description')] =
      onCallItem.Description || '';
    popupData[this.translate.instant('OnCallTable.Department')] =
      onCallItem.DepartmentDescription || '';
    popupData[this.translate.instant('OnCallTable.Property')] =
      onCallItem.VehicleTag || onCallItem.UserTag || '';
    popupData[this.translate.instant('OnCallTable.OnCallStatus')] =
      onCallItem.OnCallStatus
        ? this.translate.instant(`OnCallStatus.${onCallItem.OnCallStatus}`)
        : '';

    return popupData;
  }

  private getLocationIconByOnCallStatus(
    onCallStatus: OnCallStatus | undefined
  ): string {
    switch (onCallStatus) {
      case OnCallStatus.OnCall:
        return LocationOnCallStatusIcon.ONCALL;
      case OnCallStatus.Idle:
        return LocationOnCallStatusIcon.IDLE;
      case OnCallStatus.NotOnCall:
        return LocationOnCallStatusIcon.NOTONCALL;
    }
    return '';
  }

  private getTooltipFromMapSettings(
    onCallLocation: OnCallDto,
    mapSettings?: CustomOnCallMapSettings
  ): string | undefined {
    let tooltip = '';

    if (mapSettings?.Description) {
      tooltip += onCallLocation.Description || '';
    }

    if (mapSettings?.UserDescription && onCallLocation.UserDescription) {
      tooltip +=
        tooltip !== ''
          ? '<br>' + onCallLocation.UserDescription
          : onCallLocation.UserDescription;
    }

    if (mapSettings?.VehicleTag && onCallLocation.VehicleTag) {
      tooltip +=
        tooltip !== ''
          ? '<br>' + onCallLocation.VehicleTag
          : onCallLocation.VehicleTag;
    }

    if (mapSettings?.UserTag && onCallLocation.UserTag) {
      tooltip +=
        tooltip !== ''
          ? '<br>' + onCallLocation.UserTag
          : onCallLocation.UserTag;
    }
    return tooltip !== '' ? tooltip : undefined;
  }
}
